import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAttendanceEventsToday, listDisciplinaryActions, listLeaveRequests, listShiftSwaps } from '../../api/hr';
import { listStockBalances } from '../../api/inventory';
import { listItems } from '../../api/masterData';
import { listTransfers, listWasteEvents } from '../../api/inventoryOps';
import { listPurchaseOrders } from '../../api/purchasing';
import { describeActivity, listAuditEvents, type AuditEventDetail } from '../../api/activity';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import { ClickableRow, DashboardSkeleton, EmptyState, ErrorNote, KPICard, SectionCard, StatusBadge, tableStyle, tdStyle, thStyle } from '../../components/common';
import { IconAlertTriangle, IconCalendar, IconRepeat, IconShield, IconTrash, IconTruck } from '../../components/icons';
import type { AttendanceEvent, DisciplinaryAction, Item, LeaveRequest, PurchaseOrder, ShiftSwap, StockBalance, Transfer, WasteEvent } from '../../types/domain';

/**
 * Supervisor workspace — branch operational oversight, distinct from
 * Manager: narrower focus on today's floor-level exceptions (attendance,
 * pending leave/shift-swap, stock/waste issues, recent activity) rather
 * than branch-wide business KPIs. Every panel reads a real table this
 * role already has (attendance_event/leave_request/shift_swap.read per
 * migration 0036 grants SUPERVISOR the same as HR/MANAGER); anything the
 * signed-in user's real permissions don't cover shows "Tanpa izin", never
 * a fabricated number.
 */
export default function SupervisorDashboard() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { activeBranchId, activeBranchWarehouseIds } = useBranch();
  const [attendanceToday, setAttendanceToday] = useState<AttendanceEvent[] | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[] | null>(null);
  const [shiftSwaps, setShiftSwaps] = useState<ShiftSwap[] | null>(null);
  const [disciplinaryActions, setDisciplinaryActions] = useState<DisciplinaryAction[] | null>(null);
  const [balances, setBalances] = useState<StockBalance[] | null>(null);
  const [wasteEvents, setWasteEvents] = useState<WasteEvent[] | null>(null);
  const [events, setEvents] = useState<AuditEventDetail[] | null>(null);
  const [pos, setPos] = useState<PurchaseOrder[] | null>(null);
  const [transfers, setTransfers] = useState<Transfer[] | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canReadAttendance = hasPermission('attendance_event', 'read');
  const canReadLeave = hasPermission('leave_request', 'read');
  const canReadShiftSwap = hasPermission('shift_swap', 'read');
  const canReadDisciplinary = hasPermission('disciplinary_action', 'read');
  const canReadBalance = hasPermission('stock_balance', 'read');
  const canReadWaste = hasPermission('waste', 'read');
  const canReadGr = hasPermission('goods_receipt', 'read');
  const canReadTransfer = hasPermission('transfer', 'read');
  const canReadItem = hasPermission('item', 'read');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      canReadAttendance ? listAttendanceEventsToday(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadLeave ? listLeaveRequests(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadShiftSwap ? listShiftSwaps(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadDisciplinary ? listDisciplinaryActions(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadBalance ? listStockBalances(undefined, activeBranchId ? activeBranchWarehouseIds : undefined) : Promise.resolve(null),
      canReadWaste ? listWasteEvents(activeBranchId ?? undefined) : Promise.resolve(null),
      listAuditEvents(15),
      canReadGr ? listPurchaseOrders(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadTransfer ? listTransfers(activeBranchId ? activeBranchWarehouseIds : undefined) : Promise.resolve(null),
      canReadItem ? listItems() : Promise.resolve([]),
    ])
      .then(([att, leave, swap, disc, bal, waste, ev, poRows, transferRows, itemRows]) => {
        if (cancelled) return;
        setAttendanceToday(att);
        setLeaveRequests(leave);
        setShiftSwaps(swap);
        setDisciplinaryActions(disc);
        setBalances(bal);
        setWasteEvents(waste);
        setEvents(ev);
        setPos(poRows);
        setTransfers(transferRows);
        setItems(itemRows);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canReadAttendance,
    canReadLeave,
    canReadShiftSwap,
    canReadDisciplinary,
    canReadBalance,
    canReadWaste,
    canReadGr,
    canReadTransfer,
    canReadItem,
    activeBranchId,
    activeBranchWarehouseIds.join(','),
  ]);

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorNote text={error} />;

  const hadirCount = attendanceToday ? new Set(attendanceToday.filter((a) => a.event_type === 'CLOCK_IN').map((a) => a.employee_id)).size : null;
  const pendingLeave = leaveRequests ? leaveRequests.filter((l) => l.status === 'REQUESTED') : [];
  const pendingSwap = shiftSwaps ? shiftSwaps.filter((s) => s.status === 'REQUESTED') : [];
  const activeDisciplinary = disciplinaryActions ? disciplinaryActions.filter((d) => d.status === 'ACTIVE') : [];
  const stockOut = balances ? balances.filter((b) => b.qty_on_hand <= 0) : [];
  const draftWaste = wasteEvents ? wasteEvents.filter((w) => w.status === 'DRAFT') : [];
  const receivablePos = pos ? pos.filter((po) => po.status === 'ISSUED' || po.status === 'PARTIALLY_RECEIVED') : [];
  const draftTransfers = transfers ? transfers.filter((t) => t.status === 'DRAFT') : [];
  const itemLabel = (id: string) => items.find((it) => it.item_id === id)?.item_code ?? id.slice(0, 8);

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--alex-card-gap)', marginBottom: 'var(--alex-card-gap)' }}>
        <KPICard label="Hadir Hari Ini" value={canReadAttendance ? (hadirCount ?? 0) : '—'} hint={canReadAttendance ? undefined : 'Tanpa izin'} icon={<IconShield size={16} />} tone="success" />
        <KPICard label="Cuti Pending" value={canReadLeave ? pendingLeave.length : '—'} hint={canReadLeave ? undefined : 'Tanpa izin'} icon={<IconCalendar size={16} />} tone="info" />
        <KPICard label="Tukar Shift Pending" value={canReadShiftSwap ? pendingSwap.length : '—'} hint={canReadShiftSwap ? undefined : 'Tanpa izin'} icon={<IconRepeat size={16} />} tone="info" />
        {canReadDisciplinary && <KPICard label="Disiplin Aktif" value={activeDisciplinary.length} icon={<IconAlertTriangle size={16} />} tone="danger" />}
        {canReadBalance && <KPICard label="SKU Stok Habis" value={stockOut.length} icon={<IconAlertTriangle size={16} />} tone={stockOut.length > 0 ? 'danger' : 'success'} />}
        {canReadWaste && <KPICard label="Waste Draft" value={draftWaste.length} icon={<IconTrash size={16} />} tone="warning" />}
        {canReadGr && <KPICard label="PO Siap Diterima" value={receivablePos.length} icon={<IconTruck size={16} />} tone="primary" />}
      </div>

      <div className="alex-grid-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          <SectionCard title="Aktivitas Cabang Terbaru">
            {!events || events.length === 0 ? (
              <EmptyState text="Belum ada aktivitas tercatat." variant="table" />
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Waktu</th>
                    <th style={thStyle}>Kejadian</th>
                    <th style={thStyle}>Aksi</th>
                    <th style={thStyle}>Referensi</th>
                  </tr>
                </thead>
                <tbody>
                  {events.slice(0, 8).map((e) => (
                    <tr key={e.event_id}>
                      <td style={tdStyle}>{new Date(e.occurred_at).toLocaleString('id-ID')}</td>
                      <td style={tdStyle}>{describeActivity(e)}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={e.action} />
                      </td>
                      <td style={tdStyle}>{e.reference ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          {canReadGr && (
            <SectionCard title="Antrean Penerimaan" action="/app/goods-receipts">
              {receivablePos.length === 0 ? (
                <EmptyState text="Tidak ada PO menunggu penerimaan." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <tbody>
                    {receivablePos.slice(0, 5).map((po) => (
                      <ClickableRow key={po.po_id} onClick={() => navigate('/app/goods-receipts')}>
                        <td style={tdStyle}>{po.po_number}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <StatusBadge status={po.status} />
                        </td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          <SectionCard title="Cuti Pending">
            {!canReadLeave && <EmptyState text="Tanpa izin leave_request.read." variant="compact" />}
            {canReadLeave && pendingLeave.length === 0 && <EmptyState text="Tidak ada cuti pending." variant="compact" />}
            {canReadLeave && pendingLeave.length > 0 && (
              <table style={tableStyle}>
                <tbody>
                  {pendingLeave.slice(0, 5).map((l) => (
                    <tr key={l.leave_id}>
                      <td style={tdStyle}>{l.leave_type}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <StatusBadge status={l.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          {canReadBalance && (
            <SectionCard title="Perlu Perhatian — Stok Habis">
              {stockOut.length === 0 ? (
                <EmptyState text="Tidak ada SKU stok habis." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <tbody>
                    {stockOut.slice(0, 5).map((b) => (
                      <tr key={`${b.warehouse_id}-${b.item_id}`}>
                        <td style={tdStyle}>{itemLabel(b.item_id)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--alex-danger)', fontWeight: 600 }}>{b.qty_on_hand}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}

          {canReadTransfer && (
            <SectionCard title="Transfer Draft" action="/app/transfers">
              {draftTransfers.length === 0 ? (
                <EmptyState text="Tidak ada transfer draft." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <tbody>
                    {draftTransfers.slice(0, 4).map((t) => (
                      <ClickableRow key={t.transfer_id} onClick={() => navigate('/app/transfers')}>
                        <td style={tdStyle}>{t.transfer_number}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <StatusBadge status={t.status} />
                        </td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}
